import { CommonModule } from '@angular/common';
import { Component, Inject, Input, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Sender } from '@sendbird/chat/message';
import { firstValueFrom } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { SendbirdChatApi } from 'src/app/shared/apis/sendbird-chat.api';
import { CSButtonComponent } from 'src/app/shared/components/cs-button/cs-button.component';
import { CsCurrencyConverterComponent } from 'src/app/shared/components/cs-currency-converter/cs-currency-converter.component';
import { CsCurrencyUnitDisplayComponent } from 'src/app/shared/components/cs-currency-unit-display/cs-currency-unit-display.component';
import { CsPopupService } from 'src/app/shared/components/cs-popup/cs-popup.service';
import { LoadingModule } from 'src/app/shared/directive/loading/loading.module';
import { PipesModule } from 'src/app/shared/pipes/pipes.module';
import { SendbirdChatService } from '../../sendbird-chat.service';

enum UserRole {
  Moderator = 'Moderator',
  User = 'User',
}

interface ChatProfileUidOnlyData {
  uid: string;
  fallbackName?: string;
  fallbackAvatar?: string;
}

interface ChatProfileDialogData {
  sender?: Sender;
  avatarUrl?: string;
  uidOnlyData?: ChatProfileUidOnlyData;
}

@UntilDestroy()
@Component({
  standalone: true,
  imports: [
    CommonModule,
    CSButtonComponent,
    LoadingModule,
    PipesModule,
    CsCurrencyConverterComponent,
    CsCurrencyUnitDisplayComponent,
  ],
  selector: 'app-chat-profile',
  templateUrl: './chat-profile.component.html',
  styleUrls: ['./chat-profile.component.scss'],
})
export class ChatProfileComponent implements OnInit {
  @Input() sender!: Sender;
  readonly UserRole = UserRole;
  role: UserRole = UserRole.User;
  avatarUrl: string = '';
  hiddenMode = false;
  isLoaded = false;
  isBlock = true;
  isSelfUser = false;
  canUseChatActions = false;
  loading = false;
  currentCurrency: string = 'USDT';
  profileData: {
    registerDate: string;
    highestMultipler: number;
    highestMultiplerBetId: string;
    totalWins: number;
    totalWagered: number;
    tipsSent: number;
    tipsReceived: number;
  } | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ChatProfileDialogData,
    private dialogRef: MatDialogRef<ChatProfileComponent>,
    private sendbirdChatApi: SendbirdChatApi,
    private sendbirdChatService: SendbirdChatService,
    private csPopupService: CsPopupService,
    public appService: AppService,
  ) {}

  async ngOnInit() {
    this.loading = true;
    this.appService.currentCurrency$.pipe(untilDestroyed(this)).subscribe(v => {
      this.currentCurrency = v.currency;
    });

    // 沒有必要資料則馬上關閉 modal
    if (!this.data.sender && !this.data.uidOnlyData?.uid) {
      this.dialogRef.close();
      return;
    }

    // 如果是 sendbird 來的
    if (this.data.sender) {
      await this.initBySender(this.data.sender, this.data.avatarUrl);
      return;
    }

    // 如果是 leaderboard 來的
    if (this.data.uidOnlyData?.uid) {
      await this.initByUidOnly(this.data.uidOnlyData);
    }
  }

  private async initBySender(sender: Sender, avatarUrl = '') {
    const uid = sender.userId;
    const { metaData = {} } = this.sendbirdChatService.userList$.value.find(user => user.userId === uid) || {};

    this.sender = sender;
    this.avatarUrl = avatarUrl || '';
    this.canUseChatActions = true;
    this.isBlock = this.sendbirdChatService.checkBlockedUser(uid);
    this.isSelfUser = this.sendbirdChatService.checkSelfUser(uid);

    if ((metaData as any)?.role === 'moderator') {
      this.role = UserRole.Moderator;
      this.isLoaded = true;
      this.loading = false;
    } else {
      try {
        const response = await firstValueFrom(this.sendbirdChatApi.getChatProfile(uid));

        if (response?.data) {
          const { avatar, isInvisible, info } = response.data;

          this.hiddenMode = isInvisible && !this.isSelfUser;
          this.profileData = info;
          this.avatarUrl = avatar || this.avatarUrl || this.appService.avatarList[0]?.processedUrl || '';
          this.isLoaded = true;
        }
      } catch (error) {
        this.hiddenMode = true;
        this.isLoaded = true;
      } finally {
        this.loading = false;
      }
    }
  }

  private async initByUidOnly(uidOnlyData: ChatProfileUidOnlyData) {
    const { uid, fallbackAvatar = '', fallbackName = '' } = uidOnlyData;
    this.sender = {
      userId: uid,
      nickname: fallbackName,
      plainProfileUrl: fallbackAvatar,
    } as Sender;
    this.avatarUrl = fallbackAvatar || this.appService.avatarList[0]?.processedUrl || '';
    this.canUseChatActions = false;
    this.isBlock = false;
    this.isSelfUser = this.sendbirdChatService.checkSelfUser(uid);

    try {
      const response = await firstValueFrom(this.sendbirdChatApi.getChatProfile(uid));

      if (!response?.data || response.data.isInvisible || !response.data.info) {
        this.dialogRef.close();
        return;
      }

      if (response?.data) {
        const { avatar, isInvisible, info, name } = response.data;

        this.hiddenMode = isInvisible && !this.isSelfUser;
        this.profileData = info;
        this.sender = {
          ...this.sender,
          nickname: name || fallbackName,
          plainProfileUrl: avatar || fallbackAvatar,
        } as Sender;
        this.avatarUrl = avatar || fallbackAvatar || this.appService.avatarList[0]?.processedUrl || '';
        this.isLoaded = true;
      }
    } catch (error) {
      this.hiddenMode = true;
      this.isLoaded = true;
    } finally {
      this.loading = false;
    }
  }

  handleCloseChatProfile() {
    this.dialogRef.close();
  }

  async handleClickBlockUser() {
    if (this.isBlock) {
      // Unblock user
      await this.sendbirdChatService.unblockUser(this.sender);
      this.isBlock = this.sendbirdChatService.checkBlockedUser(this.sender.userId);
    } else {
      this.csPopupService.info({
        header: 'SILENCE',
        title: `You are going to silence user @${this.sender.nickname}`,
        message:
          'Silencing them means you won’t be able to see their messages. To undo this, please go to your settings page.',
        confirmText: 'Silence this user',
        color: 'danger',
        textAlign: 'left',
        confirmCallback: async () => {
          // Block user
          await this.sendbirdChatService.blockUser(this.sender);
          this.isBlock = this.sendbirdChatService.checkBlockedUser(this.sender.userId);
        },
      });
    }
  }
  handleClickTip() {}

  handleClickHighestBet() {}
}
